import type { CaseFileRecord } from "@/components/MedicalChronology/Files/types";
import { CaseFileService } from "@/services/caseFileService";
import { useEffect, useMemo } from "react";
import useSWR, { mutate } from "swr";

const CASE_FILES_KEY = "/case-files";

export const useCaseFiles = (caseId: string | undefined) => {
  const caseFileService = useMemo(() => new CaseFileService(), []);
  const cacheKey = caseId ? [CASE_FILES_KEY, caseId] : null;

  const {
    data: files,
    error,
    isLoading,
    mutate: revalidate,
  } = useSWR<CaseFileRecord[]>(cacheKey, () => caseFileService.getCaseFiles(caseId!), {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    dedupingInterval: 30_000,
  });

  const safeFiles = files ?? [];

  const hasProcessingFiles = useMemo(
    () => safeFiles.some((f) => f.processing_status === 0),
    [safeFiles]
  );

  useEffect(() => {
    if (!hasProcessingFiles) return;
    const timeoutId = window.setTimeout(() => revalidate(), 10_000);
    return () => window.clearTimeout(timeoutId);
  }, [hasProcessingFiles, safeFiles, revalidate]);

  const fileMapping = useMemo(() => {
    return safeFiles.reduce<Record<string, string>>((acc, file) => {
      const fileName = file.filename
        ? file.filename
        : file.bucket_url
          ? (file.bucket_url.split("/").pop() ?? "Unknown file")
          : "Unknown file";
      acc[file.file_id] = fileName;
      return acc;
    }, {});
  }, [safeFiles]);

  return {
    files: safeFiles,
    fileMapping,
    isLoading,
    error: error ? String(error) : null,
    revalidate,
  };
};

export const invalidateCaseFilesCache = (caseId?: string) => {
  if (caseId) {
    mutate([CASE_FILES_KEY, caseId]);
  } else {
    mutate((key: unknown) => Array.isArray(key) && key[0] === CASE_FILES_KEY, undefined, {
      revalidate: true,
    });
  }
};
